import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { groupBookings, legsOf } from './utils/bookingGrouping';

/**
 * 1. BASE CONFIGURATION
 */
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * 2. AUTHENTICATION INTERCEPTOR
 * Automatically attaches the Firebase Bearer token to every request.
 */
apiClient.interceptors.request.use(async (config) => {
  const auth = getAuth();
  const user = auth.currentUser;

  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

/**
 * 3. ERROR HANDLING INTERCEPTOR
 * Standardizes error responses for the UI to consume.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const message = error.response?.data?.message || 'An unexpected error occurred';
    const requiresAuth = error.response?.data?.requiresAuth;
    const redirectUrl = error.response?.data?.redirectUrl;

    if (error.response?.status === 403 && error.config?.url?.includes('/operator/')) {
      const auth = getAuth();
      if (auth.currentUser) auth.currentUser.getIdToken(true).catch(() => {});
    }

    if (error.response?.status === 401 || requiresAuth) {
      console.warn('Authentication required');

      // If the server indicates auth is required with a redirect URL
      if (requiresAuth && redirectUrl) {
        const currentPath = window.location.pathname + window.location.search;

        // Already on (or already redirecting to) the login page — skip re-storing/re-redirecting
        if (currentPath === redirectUrl || currentPath === '/login') {
          return new Promise(() => {});
        }

        // Store the current path for redirect after login
        if (currentPath !== '/') {
          sessionStorage.setItem('redirectAfterLogin', currentPath);
        }

        // Redirect to login
        window.location.href = redirectUrl;
        return new Promise(() => {}); // Prevent further error handling
      }
    }

    return Promise.reject({
      ...error.response?.data,
      status: error.response?.status,
      message: message,
      requiresAuth: requiresAuth,
      redirectUrl: redirectUrl,
      originalError: error
    });
  }
);

/**
 * 4. API MODULES
 */

export const listingService = {
  getAll: () => apiClient.get('/listings').then(res => res.data),

  getById: (id) => apiClient.get(`/listings/${id}`).then(res => res.data),

  getAvailable: (dateTime, operatorID) =>
    apiClient.get('/listings/available', { params: { dateTime, operatorID } }).then(res => res.data),

  updateById: (id, data) => apiClient.put(`/listings/${id}`, data).then(res => res.data),
  
  getByOperator: (operatorID) =>
    apiClient.get(`/listings/operator/${operatorID}`).then(res => res.data),

  create: (listingData) =>
    apiClient.post('/listings/create', listingData).then(res => res.data),

  deleteField: (id) =>
    apiClient.delete(`/listings/${id}`).then(res => res.data),

  getRestrictions: (listingID) =>
    apiClient.get(`/listings/${listingID}/booking-restrictions`).then(res => res.data),

  saveRestrictions: (operatorID, listingID, data) =>
    apiClient.put(`/operator/${operatorID}/listings/${listingID}/booking-restrictions`, data).then(res => res.data),

  getSchedule: (listingID) =>
    apiClient.get(`/listings/${listingID}/schedule`).then(res => res.data),

  getBlocks: (listingID) =>
    apiClient.get(`/listings/${listingID}/blocks`).then(res => res.data),

  listField: (operatorID, listingID, mode = 'active') =>
    apiClient.post(`/operator/${operatorID}/listings/${listingID}/list`, { mode }).then(res => res.data),
};

export const bookingService = {
  getBookedTimes: (listingID) =>
    apiClient.get(`/listings/${listingID}/booked-times`).then(res => res.data),

  createCheckoutSession: (bookingData) =>
    apiClient.post('/bookings/create-checkout-session', bookingData).then(res => res.data),
  
  resumeCheckoutSession: (bookingID) =>
    apiClient.get(`/bookings/${bookingID}/resume-checkout`).then(res => res.data),
  
  // Returns the group item (legs sharing a status) that holds this leg.
  getById: (bookingID) =>
    apiClient.get(`/bookings/${bookingID}`).then(res => groupBookings(res.data)
      .find(b => legsOf(b).some(l => String(l.bookingID) === String(bookingID)))),

  getByRenter: (renterID) =>
    apiClient.get(`/bookings/renter/${renterID}`).then(res => res.data),

  getByOperator: (operatorID) =>
    apiClient.get(`/operator/${operatorID}/bookings`).then(res => res.data),

  getPendingBookings: (operatorID) =>
    apiClient.get(`/operator/${operatorID}/pending-bookings`).then(res => res.data),

  getConfirmedBookings: (operatorID) =>
    apiClient.get(`/operator/${operatorID}/confirmed-bookings`).then(res => res.data),
  
  approveBooking: (operatorID, bookingID) =>
    apiClient.post(`/operator/${operatorID}/bookings/${bookingID}/approve`).then(res => res.data),

  rejectBooking: (operatorID, bookingID, data) =>
    apiClient.post(`/operator/${operatorID}/bookings/${bookingID}/reject`, data).then(res => res.data),

  cancelBooking: (operatorID, bookingID, data) =>
    apiClient.post(`/operator/${operatorID}/bookings/${bookingID}/cancel`, data).then(res => res.data),

  cancelRenterBooking: (bookingID, data) =>
    apiClient.post(`/bookings/${bookingID}/cancel`, data).then(res => res.data),

  getSchedule: (operatorID, listingID) =>
    apiClient.get(`/operator/${operatorID}/listings/${listingID}/schedule`).then(res => res.data),

  createSchedule: (operatorID, listingID, data) =>
    apiClient.post(`/operator/${operatorID}/listings/${listingID}/schedule`, data).then(res => res.data),

  updateSchedule: (operatorID, listingID, scheduleID, data) =>
    apiClient.put(`/operator/${operatorID}/listings/${listingID}/schedule/${scheduleID}`, data).then(res => res.data),

  deleteSchedule: (operatorID, listingID, scheduleID) =>
    apiClient.delete(`/operator/${operatorID}/listings/${listingID}/schedule/${scheduleID}`).then(res => res.data),

  getBlocks: (operatorID, listingID) =>
    apiClient.get(`/operator/${operatorID}/listings/${listingID}/blocks`).then(res => res.data),

  createBlock: (operatorID, listingID, data) =>
    apiClient.post(`/operator/${operatorID}/listings/${listingID}/blocks`, data).then(res => res.data),

  createBlockSeries: (operatorID, listingID, data) =>
    apiClient.post(`/operator/${operatorID}/listings/${listingID}/blocks/series`, data).then(res => res.data),

  updateBlock: (operatorID, listingID, blockID, data) =>
    apiClient.put(`/operator/${operatorID}/listings/${listingID}/blocks/${blockID}`, data).then(res => res.data),

  updateBlockSeries: (operatorID, listingID, seriesID, data) =>
    apiClient.put(`/operator/${operatorID}/listings/${listingID}/blocks/series/${seriesID}`, data).then(res => res.data),

  deleteBlock: (operatorID, listingID, blockID) =>
    apiClient.delete(`/operator/${operatorID}/listings/${listingID}/blocks/${blockID}`).then(res => res.data),

  deleteBlockSeries: (operatorID, listingID, seriesID) =>
    apiClient.delete(`/operator/${operatorID}/listings/${listingID}/blocks/series/${seriesID}`).then(res => res.data),

  checkAvailability: (operatorID, listingID, startDateTime, endDateTime) =>
    apiClient.post(`/operator/${operatorID}/listings/${listingID}/check-availability`, {
      startDateTime,
      endDateTime
    }).then(res => res.data),
};

export const userService = {
  getProfile: (uid) =>
    apiClient.get(`/users/${uid}`).then(res => res.data),

  updateProfile: (uid, data) => 
    apiClient.put(`/users/${uid}`, data).then(res => res.data),

  updateTimezone: (timezone) =>
    apiClient.put(`/users/timezone`, { timezone }).then(res => res.data),

  canOperate: (operatorId) =>
    apiClient.get(`/can-operate/${operatorId}`).then(res => res.data),

  createProfile: (userData) =>
    apiClient.post('/users', userData).then(res => res.data),

  deleteProfile: (uid) =>
    apiClient.delete(`/users/${uid}`).then(res => res.data),

  createClub: (clubData) =>
    apiClient.post('/clubs', clubData).then(res => res.data),

  getOperatorInfo: (operatorID) =>
    apiClient.get(`/operatorInfo/${operatorID}`).then(res => res.data),

  updateOperatorInfo: (operatorID, data) => 
    apiClient.put(`/operatorInfo/edit/${operatorID}`, data).then(res => res.data),

  addOperatorStripeID: (operatorID, data) =>
    apiClient.put(`/operatorInfo/add-stripe/${operatorID}`, data).then(res => res.data),

  getTeam: (operatorID) =>
    apiClient.get(`/operator/${operatorID}/team`).then(res => res.data),

  sendInvitation: (operatorID, data) =>
    apiClient.post(`/operator/${operatorID}/invitations`, data).then(res => res.data),

  revokeInvitation: (operatorID, invitationID) =>
    apiClient.delete(`/operator/${operatorID}/invitations/${invitationID}`).then(res => res.data),

  updateMemberNotifications: (operatorID, userID, enabled) =>
    apiClient.patch(`/operator/${operatorID}/members/${userID}/notifications`, { email_notifications: enabled ? 1 : 0 }).then(res => res.data),

  removeMember: (operatorID, userID) =>
    apiClient.delete(`/operator/${operatorID}/members/${userID}`).then(res => res.data),
};

export const invitationService = {
  getPending: () =>
    apiClient.get('/invitations/pending').then(res => res.data),

  accept: (invitationID) =>
    apiClient.post(`/invitations/${invitationID}/accept`).then(res => res.data),

  decline: (invitationID) =>
    apiClient.post(`/invitations/${invitationID}/decline`).then(res => res.data),
};

export const stripeService = {
  createAccount: (data) =>
    apiClient.post('/stripe/account', data).then(res => res.data),

  createAccountLink: (data) =>
    apiClient.post('/stripe/account-link', data).then(res => res.data),

  getStatus: (uid) =>
    apiClient.get(`/stripe/account/${uid}`).then(res => res.data),

  getLoginLink: (operatorId) =>
    apiClient.get(`/stripe/login-link/${operatorId}`).then(res => res.data),
};

export const platformService = {
  getFee: () => apiClient.get('/platform/fee').then(res => res.data),
};

export const calendarFeedService = {
  getFeed: (operatorID) =>
    apiClient.get(`/operator/${operatorID}/calendar-feed`).then(res => res.data),

  rotateFeed: (operatorID) =>
    apiClient.post(`/operator/${operatorID}/calendar-feed/rotate`).then(res => res.data),
};

export const imageService = {
  // Upload image to S3
  uploadImage: (file, type, id) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type); // 'club' or 'listing'
    formData.append('id', id);
    
    return apiClient.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  // Get all image URLs for a listing
  getListingImageUrls: (listingId) =>
    apiClient.get(`/listings/${listingId}/images`).then(res => res.data),

  // Update club logo
  updateClubLogo: (clubId, imageUrl) =>
    apiClient.put(`/clubs/${clubId}/logo`, { imageUrl }).then(res => res.data),

  // Update listing image
  updateListingImage: (listingId, imageUrl) =>
    apiClient.put(`/listings/${listingId}/image`, { imageUrl }).then(res => res.data),

  // Delete image from S3
  deleteImage: (type, id, filename) =>
    apiClient.delete(`/images/${type}/${id}/${filename}`).then(res => res.data),
};

export default apiClient;
